import type React from 'react';
import { useCallback, useState } from 'react';

import {
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import StandardButton from 'components/buttons/StandardButton';
import TMXDialog from 'components/dialogs/TMXDialog';
import FormErrorMessage from 'components/forms/FormErrorMessage';
import CollapsableParticipantGrid from 'components/participants/CollapsableParticipantGrid';
import { type ParticipantGridItem } from 'components/participants/ParticipantsNamesGrid';
import { FormLabelTextLarge, H3 } from 'components/typography/Typography';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import SendMessageSafePlayWarning from './SendMessageSafePlayWarning';
import { useStyles } from './style';

interface SendMessageDialogProps {
  isOpen?: boolean;
  showParticipants?: boolean;
  participants: ParticipantInterface[];
  onClose: () => void;
  onExited?: () => void;
  onSend: (params: SendMessageParams) => Promise<void>;
  loading?: boolean;
  title?: string;
  withReplyTo?: boolean;
  withSubject?: boolean;
  withSafePlayWarning?: boolean;
  withSendCopyOptions?: boolean;
  characterLimit?: number;
}

export interface SendMessageParams {
  message: string;
  personIds: string[];
  subject?: string;
  copyToSender?: boolean;
  copyToTournamentStaff?: boolean;
  replyTo?: string;
}

const SendMessageDialog: React.FC<React.PropsWithChildren<SendMessageDialogProps>> = ({
  isOpen,
  participants,
  onClose,
  onExited,
  showParticipants,
  onSend,
  loading,
  title,
  withReplyTo,
  withSubject,
  withSendCopyOptions,
  characterLimit,
  withSafePlayWarning,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>();

  const [includedParticipants, setIncludedParticipants] = useState(participants);
  const removeParticipant = useCallback(
    (item: ParticipantGridItem) => {
      setIncludedParticipants(includedParticipants.filter((p) => p.participantId !== item.participantId));
    },
    [includedParticipants],
  );

  const paperProps = { style: { padding: 20 } };

  const handleMessageChange = useCallback((event) => {
    setMessage(event.target.value);
  }, []);

  const [replyTo, setReplyTo] = useState<string>('');
  const handleReplyToChange = useCallback((event) => {
    setReplyTo(event.target.value);
  }, []);

  const [subject, setSubject] = useState<string>('');
  const handleSubjectChange = useCallback((event) => {
    setSubject(event.target.value);
  }, []);

  const [sendToSelf, setSendToSelf] = useState(false);
  const [sendToStaff, setSendToStaff] = useState(false);
  const handleSendToSelfChange = useCallback((_, checked) => setSendToSelf(checked), []);
  const handleSendToStaffChange = useCallback((_, checked) => setSendToStaff(checked), []);

  const handleSend = useCallback(async () => {
    if (message.length === 0) {
      setError(t('messageNotEmpty'));
    } else {
      const selectedParticipantsIds = includedParticipants.map((p) => p.person?.personId);
      await onSend({
        subject,
        message,
        replyTo,
        copyToSender: sendToSelf,
        copyToTournamentStaff: sendToStaff,
        personIds: selectedParticipantsIds,
      });
      onClose();
    }
  }, [includedParticipants, message, onClose, onSend, t, subject, sendToSelf, sendToStaff, replyTo]);

  const handleClearMessage = useCallback(() => {
    setMessage('');
  }, []);

  const handleOnEnter = useCallback(() => {
    setIncludedParticipants(participants);
  }, [participants]);

  return (
    <TMXDialog
      fullWidth
      id="send-message-dialog"
      open={isOpen}
      PaperProps={paperProps}
      TransitionProps={{
        onEnter: () => {
          setError('');
          setMessage('');
        },
        onEntered: handleOnEnter,
        onExited,
      }}
      onClose={onClose}
    >
      {!!title && (
        <DialogTitle id="send-message-dialog-title">
          <H3>{title}</H3>
        </DialogTitle>
      )}
      <DialogContent>
        {showParticipants && (
          <CollapsableParticipantGrid
            nameStyle="person-reversed-uppercase-surname"
            participants={includedParticipants}
            onDelete={removeParticipant}
          />
        )}
        {withReplyTo && (
          <Grid display="flex" gap={1} id={'replyEmail'} marginY={2}>
            <FormLabelTextLarge minWidth={90}>{t('replyEmail')}</FormLabelTextLarge>
            <TextField fullWidth id={'replyEmailInput'} size="small" value={replyTo} onChange={handleReplyToChange} />
          </Grid>
        )}
        {withSubject && (
          <Grid display="flex" gap={1} id={'subject'} marginY={2}>
            <FormLabelTextLarge minWidth={90}>{t('subject')}</FormLabelTextLarge>
            <TextField fullWidth id={'subjectInput'} size="small" value={subject} onChange={handleSubjectChange} />
          </Grid>
        )}
        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder={t('enterMessage')}
          value={message}
          variant="outlined"
          onChange={handleMessageChange}
          {...(characterLimit && {
            inputProps: { maxLength: characterLimit },
          })}
        />
        <Grid container justifyContent="space-between">
          <Grid item className={classes.clearText} marginTop={1} onClick={handleClearMessage}>
            <Typography color="primary">{t('clearText')}</Typography>
          </Grid>
          {!!characterLimit && (
            <Grid item>
              <Typography className={classes.charactersLeft}>
                {characterLimit - message?.length} {t('charactersRemaining')}
              </Typography>
            </Grid>
          )}
        </Grid>
        {withSendCopyOptions && (
          <Grid marginY={2}>
            <FormControlLabel
              checked={sendToSelf}
              control={<Checkbox id="send-self-copy" size="small" />}
              label={t('sendSelfCopy')}
              onChange={handleSendToSelfChange}
            />
            <FormControlLabel
              checked={sendToStaff}
              control={<Checkbox id="send-staff-copy" size="small" />}
              label={t('sendStaffCopy')}
              onChange={handleSendToStaffChange}
            />
          </Grid>
        )}
        {withSafePlayWarning && <SendMessageSafePlayWarning />}
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <Grid container justifyContent="flex-end" spacing={2}>
          <Grid item>
            <StandardButton onClick={onClose}>{t('ccl')}</StandardButton>
          </Grid>
          <Grid item>
            <StandardButton
              disabled={includedParticipants.length === 0}
              id="send-message-dialog-send-button"
              loading={loading}
              variant="primary"
              onClick={handleSend}
            >
              {t('send')}
            </StandardButton>
          </Grid>
        </Grid>
      </DialogActions>
      {!!error && <FormErrorMessage message={error} />}
    </TMXDialog>
  );
};

export default SendMessageDialog;
